// modules/CorrosionLayerSystem.js
// 抗腐蚀图层系统模块
import DebugHelper from './DebugHelper.js';
import VehicleEquipmentSystem from './VehicleEquipmentSystem.js';

export default class CorrosionLayerSystem {
    /**
     * 计算移动距离（基于瓦片坐标）
     * @param {number} fromX - 起始X坐标
     * @param {number} fromY - 起始Y坐标
     * @param {number} toX - 目标X坐标
     * @param {number} toY - 目标Y坐标
     * @returns {number} 移动距离（瓦片数）
     */
    static calculateDistance(fromX, fromY, toX, toY) {
        const dx = Math.abs(toX - fromX);
        const dy = Math.abs(toY - fromY);
        return Math.max(dx, dy); // 使用切比雪夫距离（允许斜向移动）
    }
    
    /**
     * 计算图层消耗
     * @param {Object} vehicle - 战车对象
     * @param {number} distance - 移动距离（瓦片数）
     * @param {boolean} isCave - 是否在洞穴中
     * @returns {number} 消耗的图层值
     */
    static calculateConsumption(vehicle, distance, isCave = false) {
        if (!vehicle || !vehicle.equipment) {
            return 0;
        }
        
        // 基础消耗：每移动1格消耗1点图层（洞穴中）
        // 非洞穴场景不消耗
        if (!isCave) {
            return 0;
        }
        
        let baseConsumption = distance; // 每格1点
        
        // 计算装备对消耗的影响
        const vehicleStats = VehicleEquipmentSystem.calculateVehicleStats(vehicle);
        
        // 抗腐蚀抗性（负数表示减少消耗）
        const resistance = vehicleStats.corrosionResistance || 0;
        // 抗腐蚀消耗（正数表示增加消耗）
        const consumption = vehicleStats.corrosionConsumption || 0;
        
        // 应用抗性和消耗加成
        const finalConsumption = Math.max(0, Math.floor(
            baseConsumption * (1 + consumption) * (1 + resistance)
        ));
        
        return finalConsumption;
    }
    
    /**
     * 消耗抗腐蚀图层
     * @param {Object} vehicle - 战车对象
     * @param {number} consumption - 消耗量
     * @returns {Object} 消耗结果 {success: boolean, layerDepleted: boolean, cpDepleted: boolean, message: string}
     */
    static consumeLayer(vehicle, consumption) {
        if (!vehicle) {
            return {
                success: false,
                message: '战车不存在'
            };
        }
        
        if (consumption <= 0) {
            return {
                success: true,
                layerDepleted: false,
                cpDepleted: false,
                message: '无需消耗'
            };
        }
        
        // 初始化corrosionLayer（如果未定义）
        if (vehicle.corrosionLayer === undefined) {
            vehicle.corrosionLayer = 0;
        }
        
        // 先消耗图层
        if (vehicle.corrosionLayer > 0) {
            vehicle.corrosionLayer = Math.max(0, vehicle.corrosionLayer - consumption);
            
            if (vehicle.corrosionLayer <= 0) {
                DebugHelper.log('CorrosionLayerSystem: 抗腐蚀图层已耗尽！');
                return {
                    success: true,
                    layerDepleted: true,
                    cpDepleted: false,
                    message: '抗腐蚀图层已耗尽！'
                };
            }
            
            return {
                success: true,
                layerDepleted: false,
                cpDepleted: false,
                message: `消耗 ${consumption} 点图层，剩余 ${vehicle.corrosionLayer} 点`
            };
        }
        
        // 图层为0，CP已耗尽
        DebugHelper.log('CorrosionLayerSystem: CP已耗尽，开始损坏装备！');
        return {
            success: true,
            layerDepleted: true,
            cpDepleted: true,
            message: 'CP已耗尽，装备开始损坏！'
        };
    }
    
    /**
     * 处理装备损坏（CP为0时）
     * @param {Object} vehicle - 战车对象
     * @returns {Array<Object>} 损坏的装备列表
     */
    static damageEquipment(vehicle) {
        if (!vehicle || !vehicle.equipment) {
            return [];
        }
        
        const damagedEquipment = [];
        
        // 获取所有装备（不包括必须装备，优先损坏非必须装备）
        const nonRequiredSlots = ['mainCannon', 'subCannon', 'se'];
        const requiredSlots = ['ai', 'engine', 'chassis'];
        
        // 先尝试损坏非必须装备
        this.damageEquipment(vehicle, nonRequiredSlots, damagedEquipment, 0.3);
        
        // 如果非必须装备都损坏了，开始损坏必须装备
        if (damagedEquipment.length === 0) {
            this.damageEquipment(vehicle, requiredSlots, damagedEquipment, 0.2);
        }
        
        return damagedEquipment;
    }
    
    /**
     * 损坏装备
     * @param {Object} vehicle - 载具对象
     * @param {Array} slots - 装备槽数组
     * @param {Array} damagedEquipment - 损坏装备数组
     * @param {number} probability - 损坏概率
     */
    damageEquipment(vehicle, slots, damagedEquipment, probability) {
        for (const slot of slots) {
            const equipment = vehicle.equipment[slot];
            if (equipment && equipment.durability > 0) {
                if (Math.random() < probability) {
                    equipment.durability = Math.max(0, equipment.durability - 1);
                    damagedEquipment.push({
                        slot: slot,
                        equipment: equipment,
                        message: `${equipment.name} 损坏了！`
                    });
                    
                    DebugHelper.log(`CorrosionLayerSystem: ${equipment.name} 损坏，耐久度: ${equipment.durability}`);
                }
            }
        }
    }
    
    /**
     * 处理人类角色在洞穴中的伤害（图层为0且SP为0时）
     * @param {Object} player - 玩家对象
     * @param {number} damage - 伤害值
     * @returns {Object} 伤害结果
     */
    static damagePlayer(player, damage = 1) {
        if (!player) {
            return {
                success: false,
                message: '玩家不存在'
            };
        }
        
        // 确保HP存在
        if (player.hp === undefined) {
            player.hp = player.maxHp || 100;
        }
        
        player.hp = Math.max(0, player.hp - damage);
        
        return {
            success: true,
            damage: damage,
            currentHp: player.hp,
            message: `受到辐射伤害 ${damage} 点，剩余HP: ${player.hp}`
        };
    }
    
    /**
     * 处理移动时的图层消耗
     * @param {Object} player - 玩家对象
     * @param {number} fromX - 起始X坐标
     * @param {number} fromY - 起始Y坐标
     * @param {number} toX - 目标X坐标
     * @param {number} toY - 目标Y坐标
     * @param {boolean} isCave - 是否在洞穴中
     * @returns {Object} 处理结果
     */
    static processMovement(player, fromX, fromY, toX, toY, isCave = false) {
        if (!isCave) {
            return {
                success: true,
                consumed: false,
                message: '非洞穴场景，不消耗图层'
            };
        }
        
        // 计算移动距离
        const distance = this.calculateDistance(fromX, fromY, toX, toY);
        if (distance === 0) {
            return {
                success: true,
                consumed: false,
                message: '未移动'
            };
        }
        
        // 如果在战车中（CP为0时移动会掉血但不强制下车，因为已经下车了）
        if (player.inVehicle && player.vehicle) {
            const vehicle = player.vehicle;
            
            // 检查CP是否为0，如果为0则强制下车并掉玩家HP
            if (vehicle.corrosionLayer <= 0) {
                const damageResult = this.damagePlayer(player, 1);
                return {
                    success: true,
                    consumed: false,
                    forceExitVehicle: true,
                    playerDamaged: true,
                    damage: damageResult.damage,
                    message: 'CP已耗尽！强制下车'
                };
            }
            
            // 计算消耗
            const consumption = this.calculateConsumption(vehicle, distance, isCave);
            
            // 消耗图层
            const result = this.consumeLayer(vehicle, consumption);
            
            // 如果消耗后CP为0，强制下车并掉玩家HP
            if (result.cpDepleted) {
                const damageResult = this.damagePlayer(player, 1);
                return {
                    success: true,
                    consumed: true,
                    layerDepleted: result.layerDepleted,
                    cpDepleted: true,
                    forceExitVehicle: true,
                    playerDamaged: true,
                    damage: damageResult.damage,
                    message: 'CP已耗尽！强制下车'
                };
            }
            
            return result;
        } else {
            // 人类角色在洞穴中（已下车）
            // 人类直接暴露在辐射环境中，每次移动都受到伤害（不管战车CP是多少）
            // 因为人类已经不在战车保护中了
            const damageResult = this.damagePlayer(player, distance); // 每移动1格掉1点HP
            return {
                success: true,
                consumed: false,
                playerDamaged: true,
                damage: damageResult.damage,
                message: '' // 不显示消息，避免频繁弹出
            };
        }
    }
}

